import { useState, useEffect } from "react";
import { useAuth, api } from "../App";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Users,
  Plus,
  ChevronLeft,
  Edit,
  Trash2,
  DollarSign,
  Shield,
  Search,
  Bell,
} from "lucide-react";

export default function UsersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get("/users");
      setUsers(res.data);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    
    try {
      await api.delete(`/users/${userId}`);
      toast.success("User deleted");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleSendNudges = async () => {
    try {
      const res = await api.post("/notifications/generate-nudge");
      toast.success(res.data.message);
    } catch (error) {
      toast.error("Failed to send nudges");
    }
  };

  const filteredUsers = users.filter((u) =>
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRoleColor = (role) => {
    const colors = {
      admin: "bg-primary/20 text-primary",
      hr: "bg-info/20 text-info",
      manager: "bg-warning/20 text-warning",
    };
    return colors[role] || "bg-surface text-text-muted";
  };

  const formatSalary = (amount) => {
    if (!amount) return "Not set";
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(1)}L`;
    }
    return `₹${amount.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary text-2xl font-heading">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      
      
      <main className="flex-1 lg:ml-64 pb-20 lg:pb-0">
        {/* Header */}
        <header className="sticky top-0 z-40 glass-effect border-b border-border">
          <div className="flex items-center justify-between p-4 lg:p-6">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/me")}
                className="text-text-secondary hover:text-text-primary lg:hidden"
              >
                <ChevronLeft className="w-6 h-6" />
              </Button>
              <div>
                <h1 className="font-heading text-2xl text-text-primary flex items-center gap-2">
                  <Users className="w-6 h-6 text-primary" />
                  TEAM MANAGEMENT
                </h1>
                <p className="text-text-muted text-sm">
                  {users.length} team members
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleSendNudges}
                variant="outline"
                className="border-warning text-warning hover:bg-warning/10 font-heading hidden lg:flex"
              >
                <Bell className="w-4 h-4 mr-2" />
                SEND NUDGES
              </Button>
              <UserModal
                open={showAddModal}
                onOpenChange={setShowAddModal}
                onSuccess={fetchUsers}
                isAdmin={user?.role === "admin"}
              />
            </div>
          </div>

          {/* Search */}
          <div className="px-4 lg:px-6 pb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or email..."
                className="pl-10 bg-background border-border text-text-primary"
              />
            </div>
          </div>
        </header>

        <div className="p-4 lg:p-6">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-surface border border-border rounded-lg p-4 text-center">
              <p className="stat-number text-primary">
                {users.filter(u => u.role === "admin").length}
              </p>
              <p className="text-text-muted text-xs">Admins</p>
            </div>
            <div className="bg-surface border border-border rounded-lg p-4 text-center">
              <p className="stat-number text-info">
                {users.filter(u => u.role === "hr").length}
              </p>
              <p className="text-text-muted text-xs">HR</p>
            </div>
            <div className="bg-surface border border-border rounded-lg p-4 text-center">
              <p className="stat-number text-warning">
                {users.filter(u => u.role === "manager").length}
              </p>
              <p className="text-text-muted text-xs">Managers</p>
            </div>
          </div>

          {/* Users List */}
          <div className="bg-surface border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-4 font-heading text-text-muted text-sm">USER</th>
                    <th className="text-left p-4 font-heading text-text-muted text-sm">ROLE</th>
                    <th className="text-left p-4 font-heading text-text-muted text-sm">SALARY</th>
                    <th className="text-left p-4 font-heading text-text-muted text-sm">STATS</th>
                    <th className="text-right p-4 font-heading text-text-muted text-sm">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredUsers.map((teamUser) => (
                    <tr key={teamUser.id} className="hover:bg-surface-highlight transition-colors">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                            <span className="text-primary font-bold">
                              {teamUser.name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="text-text-primary font-medium">
                              {teamUser.name}
                              {teamUser.id === user?.id && (
                                <span className="ml-2 text-primary text-xs">(You)</span>
                              )}
                            </p>
                            <p className="text-text-muted text-sm">{teamUser.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleColor(teamUser.role)}`}>
                          {teamUser.role?.toUpperCase()}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="font-mono text-text-primary">
                          {formatSalary(teamUser.salary)}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-4 text-sm text-text-muted">
                          <span>{teamUser.stats?.candidates_reviewed || 0} reviews</span>
                          <span>{teamUser.stats?.hires_made || 0} hires</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingUser(teamUser)}
                            className="text-text-muted hover:text-primary"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {user?.role === "admin" && teamUser.id !== user?.id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteUser(teamUser.id)}
                              className="text-text-muted hover:text-error"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Edit User Modal */}
        {editingUser && (
          <UserModal
            open={!!editingUser}
            onOpenChange={() => setEditingUser(null)}
            onSuccess={() => {
              fetchUsers();
              setEditingUser(null);
            }}
            user={editingUser}
            isAdmin={user?.role === "admin"}
          />
        )}
      </main>

      
    </div>
  );
}

function UserModal({ open, onOpenChange, onSuccess, user: editUser, isAdmin }) {
  const [formData, setFormData] = useState({
    name: editUser?.name || "",
    email: editUser?.email || "",
    password: "",
    role: editUser?.role || "manager",
    salary: editUser?.salary || "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (editUser) {
      setFormData({
        name: editUser.name || "",
        email: editUser.email || "",
        password: "",
        role: editUser.role || "manager",
        salary: editUser.salary || "",
      });
    }
  }, [editUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editUser) {
        // Update user
        const updateData = {
          name: formData.name,
          salary: formData.salary ? parseFloat(formData.salary) : null,
        };
        if (isAdmin) {
          updateData.role = formData.role;
        }
        await api.put(`/users/${editUser.id}`, updateData);
        toast.success("User updated!");
      } else {
        // Create user
        await api.post("/auth/register", {
          ...formData,
          salary: formData.salary ? parseFloat(formData.salary) : null,
        });
        toast.success("Team member added! 🎉");
      }
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {!editUser && (
        <DialogTrigger asChild>
          <Button className="bg-primary hover:bg-primary-hover text-white font-heading neon-glow">
            <Plus className="w-4 h-4 mr-2" />
            ADD MEMBER
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="bg-surface border-border">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-text-primary">
            {editUser ? "EDIT TEAM MEMBER" : "ADD TEAM MEMBER"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label className="text-text-secondary">Full Name *</Label>
            <Input
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="bg-background border-border text-text-primary"
              placeholder="John Doe"
            />
          </div>

          {!editUser && (
            <>
              <div className="space-y-2">
                <Label className="text-text-secondary">Email *</Label>
                <Input
                  required
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-background border-border text-text-primary"
                  placeholder="john@dentsuamp.com"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-text-secondary">Password *</Label>
                <Input
                  required
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="bg-background border-border text-text-primary"
                  placeholder="Strong password"
                />
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-text-secondary">Role</Label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                disabled={!isAdmin && editUser}
                className="w-full h-10 px-3 rounded-md bg-background border border-border text-text-primary focus:border-primary focus:outline-none disabled:opacity-50"
              >
                <option value="manager">Manager / Interviewer</option>
                <option value="hr">HR</option>
                {isAdmin && <option value="admin">Admin</option>}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-text-secondary">Salary (₹)</Label>
              <Input
                type="number"
                value={formData.salary}
                onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                className="bg-background border-border text-text-primary"
                placeholder="1500000"
              />
            </div>
          </div>

          <div className="bg-background/50 rounded-lg p-3 text-sm text-text-muted">
            <Shield className="w-4 h-4 inline mr-2 text-warning" />
            Salary is used to filter which candidates a manager can review.
          </div>

          <div className="flex gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 border-border text-text-primary"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-primary hover:bg-primary-hover text-white font-heading"
            >
              {submitting ? "Saving..." : editUser ? "UPDATE" : "ADD MEMBER"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
